import {
	fromString,
	lchaToRgba,
	rgbaToLcha,
	toString,
	withAlpha,
} from 'ol/color.js';
import {ColorType, LiteralExpression, Ops, parse} from 'ol/expr/expression.js';


export function buildExpression(encoded, type, context) {
	const expression = parse(encoded, type, context);
	return compileExpression(expression, context);
}

export function compileExpression(expression, context) {
	if (expression instanceof LiteralExpression) {
		// convert colors to array if possible
		if (expression.type === ColorType && typeof expression.value === 'string') {
			const colorValue = fromString(expression.value);
			return function () {
				return colorValue;
			};
		}else if(typeof expression.value === 'string' && expression.value.startsWith(Ops.Var+':')) {
			let parts = expression.value.split(':');
			let name = parts[1];
			return function (context) {
				return context.variables[name];
			};
		}
		return function () {
			return expression.value;
		};
	}
	const operator = expression.operator;
	switch (operator) {
		case Ops.Number:
		case Ops.String:
		case Ops.Coalesce: {
			return compileAssertionExpression(expression, context);
		}
		case Ops.Get:
		case Ops.Var:
		case Ops.Has: {
			return compileAccessorExpression(expression, context);
		}
		case Ops.Id: {
			return (context) => context.featureId;
		}
		case Ops.GeometryType: {
			return (context) => context.geometryType;
		}
		case Ops.Concat: {
			const args = expression.args.map((e) => compileExpression(e, context));
			return (context) =>
				''.concat(...args.map((arg) => arg(context).toString()));
			}
		case Ops.Resolution: {
			return (context) => context.resolution;
		}
		case Ops.Any:
		case Ops.All:
		case Ops.Between:
		case Ops.In:
		case Ops.Not: {
			return compileLogicalExpression(expression, context);
		}
		case Ops.Equal:
		case Ops.NotEqual:
		case Ops.LessThan:
		case Ops.LessThanOrEqualTo:
		case Ops.GreaterThan:
		case Ops.GreaterThanOrEqualTo: {
			return compileComparisonExpression(expression, context);
		}
		case Ops.Multiply:
		case Ops.Divide:
		case Ops.Add:
		case Ops.Subtract:
		case Ops.Clamp:
		case Ops.Mod:
		case Ops.Pow:
		case Ops.Abs:
		case Ops.Floor:
		case Ops.Ceil:
		case Ops.Round:
		case Ops.Sin:
		case Ops.Cos:
		case Ops.Atan:
		case Ops.Sqrt: {
			return compileNumericExpression(expression, context);
		}
		case Ops.Case: {
			return compileCaseExpression(expression, context);
		}
		case Ops.Match: {
			return compileMatchExpression(expression, context);
		}
		case Ops.Interpolate: {
			return compileInterpolateExpression(expression, context);
		}
		case Ops.ToString: {
			return compileConvertExpression(expression, context);
		}
		default: {
			throw new Error(`Unsupported operator ${operator}`);
		}
		// TODO: unimplemented
		// Ops.Zoom
		// Ops.Time
		// Ops.Array
		// Ops.Color
		// Ops.Band
		// Ops.Palette
	}
}


export function compileAssertionExpression(expression, context) {
const type = expression.operator;
const length = expression.args.length;

const args = new Array(length);
for (let i = 0; i < length; ++i) {
	args[i] = compileExpression(expression.args[i], context);
}
switch (type) {
	case Ops.Coalesce: {
	return (context) => {
		for (let i = 0; i < length; ++i) {
		const value = args[i](context);
		if (typeof value !== 'undefined' && value !== null) {
			return value;
		}
		}
		throw new Error('Expected one of the values to be non-null');
	};
	}
	case Ops.Number:
	case Ops.String: {
	return (context) => {
		for (let i = 0; i < length; ++i) {
		const value = args[i](context);
		if (typeof value === type) {
			return value;
		}
		}
		throw new Error(`Expected one of the values to be a ${type}`);
	};
	}
	default: {
	throw new Error(`Unsupported assertion operator ${type}`);
	}
}
}


export function compileAccessorExpression(expression, context) {
	const nameExpression = /** @type {LiteralExpression} */ (expression.args[0]);
	const name = /** @type {string} */ (nameExpression.value);
	switch (expression.operator) {
		case Ops.Get: {
			return (context) => {
				const args = expression.args;
				let value = context.properties[name];
				for (let i = 1, ii = args.length; i < ii; ++i) {
				const keyExpression = /** @type {LiteralExpression} */ (args[i]);
				const key = /** @type {string|number} */ (keyExpression.value);
				value = value[key];
				}
				return value;
			};
		}
		case Ops.Var: {
			return (context) => context.variables[name];
		}
		case Ops.Has: {
			return (context) => {
				const args = expression.args;
				if (!(name in context.properties)) {
				return false;
				}
				let value = context.properties[name];
				for (let i = 1, ii = args.length; i < ii; ++i) {
				const keyExpression = /** @type {LiteralExpression} */ (args[i]);
				const key = /** @type {string|number} */ (keyExpression.value);
				if (!value || !Object.hasOwn(value, key)) {
					return false;
				}
				value = value[key];
				}
				return true;
			};
		}
		default: {
			throw new Error(`Unsupported accessor operator ${expression.operator}`);
		}
	}
}


export function compileComparisonExpression(expression, context) {
	const op = expression.operator;
	const left = compileExpression(expression.args[0], context);
	const right = compileExpression(expression.args[1], context);
	switch (op) {
		case Ops.Equal: {
		return (context) => left(context) === right(context);
		}
		case Ops.NotEqual: {
		return (context) => left(context) !== right(context);
		}
		case Ops.LessThan: {
		return (context) => left(context) < right(context);
		}
		case Ops.LessThanOrEqualTo: {
		return (context) => left(context) <= right(context);
		}
		case Ops.GreaterThan: {
		return (context) => left(context) > right(context);
		}
		case Ops.GreaterThanOrEqualTo: {
		return (context) => left(context) >= right(context);
		}
		default: {
		throw new Error(`Unsupported comparison operator ${op}`);
		}
	}
}


export function compileLogicalExpression(expression, context) {
	const op = expression.operator;
	const length = expression.args.length;

	const args = new Array(length);
	for (let i = 0; i < length; ++i) {
		args[i] = compileExpression(expression.args[i], context);
	}
	switch (op) {
		case Ops.Any: {
		return (context) => {
			for (let i = 0; i < length; ++i) {
			if (args[i](context)) {
				return true;
			}
			}
			return false;
		};
		}
		case Ops.All: {
			return (context) => {
				for (let i = 0; i < length; ++i) {
					if (!args[i](context)) {
						return false;
					}
				}
				return true;
			};
		}
		case Ops.Between: {
			return (context) => {
				const value = args[0](context);
				const min = args[1](context);
				const max = args[2](context);
				return value >= min && value <= max;
			};
		}
		case Ops.In: {
			return (context) => {
				const value = args[0](context);
				for (let i = 1; i < length; ++i) {
					let _values = args[i](context);
					if (_values.constructor === Array && _values.includes(value) || value === _values) {
						return true;
					}
				}
				return false;
			};
		}
		case Ops.Not: {
			return (context) => !args[0](context);
		}
		default: {
			throw new Error(`Unsupported logical operator ${op}`);
		}
	}
}


export function compileNumericExpression(expression, context) {
const op = expression.operator;
const length = expression.args.length;

const args = new Array(length);
for (let i = 0; i < length; ++i) {
	args[i] = compileExpression(expression.args[i], context);
}
switch (op) {
	case Ops.Multiply: {
	return (context) => {
		let value = 1;
		for (let i = 0; i < length; ++i) {
		value *= args[i](context);
		}
		return value;
	};
	}
	case Ops.Divide: {
	return (context) => args[0](context) / args[1](context);
	}
	case Ops.Add: {
	return (context) => {
		let value = 0;
		for (let i = 0; i < length; ++i) {
		value += args[i](context);
		}
		return value;
	};
	}
	case Ops.Subtract: {
	return (context) => args[0](context) - args[1](context);
	}
	case Ops.Clamp: {
	return (context) => {
		const value = args[0](context);
		const min = args[1](context);
		if (value < min) {
		return min;
		}
		const max = args[2](context);
		if (value > max) {
		return max;
		}
		return value;
	};
	}
	case Ops.Mod: {
	return (context) => args[0](context) % args[1](context);
	}
	case Ops.Pow: {
	return (context) => Math.pow(args[0](context), args[1](context));
	}
	case Ops.Abs: {
	return (context) => Math.abs(args[0](context));
	}
	case Ops.Floor: {
	return (context) => Math.floor(args[0](context));
	}
	case Ops.Ceil: {
	return (context) => Math.ceil(args[0](context));
	}
	case Ops.Round: {
	return (context) => Math.round(args[0](context));
	}
	case Ops.Sin: {
	return (context) => Math.sin(args[0](context));
	}
	case Ops.Cos: {
	return (context) => Math.cos(args[0](context));
	}
	case Ops.Atan: {
	if (length === 2) {
		return (context) => Math.atan2(args[0](context), args[1](context));
	}
	return (context) => Math.atan(args[0](context));
	}
	case Ops.Sqrt: {
	return (context) => Math.sqrt(args[0](context));
	}
	default: {
	throw new Error(`Unsupported numeric operator ${op}`);
	}
}
}


export function compileCaseExpression(expression, context) {
const length = expression.args.length;
const args = new Array(length);
for (let i = 0; i < length; ++i) {
	args[i] = compileExpression(expression.args[i], context);
}
return (context) => {
	for (let i = 0; i < length - 1; i += 2) {
	const condition = args[i](context);
	if (condition) {
		return args[i + 1](context);
	}
	}
	return args[length - 1](context);
};
}


export function compileMatchExpression(expression, context) {
const length = expression.args.length;
const args = new Array(length);
for (let i = 0; i < length; ++i) {
	args[i] = compileExpression(expression.args[i], context);
}
return (context) => {
	const value = args[0](context);
	for (let i = 1; i < length - 1; i += 2) {
	if (value === args[i](context)) {
		return args[i + 1](context);
	}
	}
	return args[length - 1](context);
};
}


export function compileInterpolateExpression(expression, context) {
const length = expression.args.length;
const args = new Array(length);
for (let i = 0; i < length; ++i) {
	args[i] = compileExpression(expression.args[i], context);
}
return (context) => {
	const base = args[0](context);
	const value = args[1](context);

	let previousInput;
	let previousOutput;
	for (let i = 2; i < length; i += 2) {
	const input = args[i](context);
	let output = args[i + 1](context);
	const isColor = Array.isArray(output);
	if (isColor) {
		output = withAlpha(output);
	}
	if (input >= value) {
		if (i === 2) {
		return output;
		}
		if (isColor) {
		return interpolateColor(
			base,
			value,
			previousInput,
			previousOutput,
			input,
			output,
		);
		}
		return interpolateNumber(
		base,
		value,
		previousInput,
		previousOutput,
		input,
		output,
		);
	}
	previousInput = input;
	previousOutput = output;
	}
	return previousOutput;
};
}


export function compileConvertExpression(expression, context) {
const op = expression.operator;
const length = expression.args.length;

const args = new Array(length);
for (let i = 0; i < length; ++i) {
	args[i] = compileExpression(expression.args[i], context);
}
switch (op) {
	case Ops.ToString: {
	return (context) => {
		const value = args[0](context);
		if (expression.args[0].type === ColorType) {
		return toString(value);
		}
		return value.toString();
	};
	}
	default: {
	throw new Error(`Unsupported convert operator ${op}`);
	}
}
}


export function interpolateNumber(base, value, input1, output1, input2, output2) {
	const delta = input2 - input1;
	if (delta === 0) {
		return output1;
	}
	const along = value - input1;
	const factor =
		base === 1
		? along / delta
		: (Math.pow(base, along) - 1) / (Math.pow(base, delta) - 1);
	return output1 + factor * (output2 - output1);
}


export function interpolateColor(base, value, input1, rgba1, input2, rgba2) {
	const delta = input2 - input1;
	if (delta === 0) {
		return rgba1;
	}
	const lcha1 = rgbaToLcha(rgba1);
	const lcha2 = rgbaToLcha(rgba2);
	let deltaHue = lcha2[2] - lcha1[2];
	if (deltaHue > 180) {
		deltaHue -= 360;
	} else if (deltaHue < -180) {
		deltaHue += 360;
	}

	const lcha = [
		interpolateNumber(base, value, input1, lcha1[0], input2, lcha2[0]),
		interpolateNumber(base, value, input1, lcha1[1], input2, lcha2[1]),
		lcha1[2] + interpolateNumber(base, value, input1, 0, input2, deltaHue),
		interpolateNumber(base, value, input1, rgba1[3], input2, rgba2[3]),
	];
	return lchaToRgba(lcha);
}